import { useState } from 'react';
import { Link } from 'react-router-dom';
import { C2Button } from '../components/c2';
import AuthShell from '../components/auth/AuthShell';
import AuthInput from '../components/auth/Input';
import OAuthStack from '../components/auth/OAuthStack';
import { api, setToken, type ApiError } from '../lib/api';
import { track } from '../lib/analytics';

const PERSONAL_DOMAINS = /(gmail|outlook|hotmail|yahoo|icloud|proton)\.com$/i;

export default function RegisterPage() {
  const [mode, setMode] = useState<'oauth' | 'email'>('oauth');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isWorkEmail = email && !PERSONAL_DOMAINS.test(email);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name || !email || !password) return;
    setLoading(true);
    try {
      const result = await api.register(name, email, password);
      sessionStorage.setItem('tgl_just_signed_up', '1');
      // Two paths from /auth/register:
      //   - Email-confirmation required (the normal flow): server returns
      //     202 { pending: true } with no token. Show a "check your email"
      //     screen; the verification link will sign them in and route to
      //     /onboarding.
      //   - Token returned (legacy / direct path): use it and go to
      //     /onboarding.
      if (result.pending && !result.token) {
        track('user_signup_pending_verification', { email });
        setPendingEmail(email);
        setLoading(false);
        return;
      }
      if (result.token) setToken(result.token);
      // user_signed_up + Google Ads conversion fire from App-level
      // flushSignupTracking() on the next mount, driven by the
      // tgl_just_signed_up sessionStorage flag set above.
      window.location.href = '/onboarding';
    } catch (err) {
      setError((err as ApiError).message || 'Sign-up failed');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!pendingEmail || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await api.resendVerification(pendingEmail);
      setResendStatus('sent');
    } catch {
      setResendStatus('idle');
    }
  };

  if (pendingEmail) {
    return (
      <AuthShell>
        <h2
          className="font-display font-extrabold text-ink m-0 mb-2.5"
          style={{ fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.1 }}
        >
          Check your email
        </h2>
        <p className="text-[15px] text-ink-soft m-0 mb-5 leading-[1.55]">
          We sent a confirmation link to <strong className="text-ink">{pendingEmail}</strong>. Click it
          to verify your email and finish setting up your workspace.
        </p>
        <div
          className="rounded-md mb-6 flex gap-2.5 items-start text-[13px] leading-[1.55] text-ink"
          style={{ background: 'var(--color-sky)', padding: '12px 16px' }}
        >
          <span className="text-base">📬</span>
          <span>Check your spam folder if it doesn't show up in a minute. The link expires in 24 hours.</span>
        </div>
        <C2Button
          variant="ghost"
          size="md"
          className="w-full"
          onClick={handleResend}
          disabled={resendStatus !== 'idle'}
        >
          {resendStatus === 'sending'
            ? 'Sending…'
            : resendStatus === 'sent'
              ? 'Sent — check your inbox'
              : 'Resend confirmation email'}
        </C2Button>
        <div className="mt-6 pt-5 border-t border-hairline text-center text-sm text-ink-soft">
          Wrong email?{' '}
          <button
            onClick={() => {
              setPendingEmail(null);
              setResendStatus('idle');
            }}
            className="bg-transparent border-0 cursor-pointer p-0 font-semibold"
            style={{ color: 'var(--accent-solid)' }}
          >
            Start over
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h2
        className="font-display font-extrabold text-ink m-0 mb-5 text-center"
        style={{ fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.1 }}
      >
        Create your account
      </h2>

      <div
        className="rounded-md mb-6 flex gap-2.5 items-start text-[13px] leading-[1.55] text-ink"
        style={{ background: 'var(--color-lemon)', padding: '12px 16px' }}
      >
        <span className="text-base">💡</span>
        <span>Use your <strong>work email</strong> so your teammates auto-join the same workspace.</span>
      </div>

      {mode === 'oauth' && <OAuthStack onEmailClick={() => setMode('email')} />}

      {mode === 'email' && (
        <>
          <button
            onClick={() => setMode('oauth')}
            className="bg-transparent border-0 cursor-pointer p-0 mb-4 text-[13px] text-ink-soft font-medium"
          >
            ← Other sign-up options
          </button>
          <form onSubmit={handleRegister}>
            <AuthInput label="First name" placeholder="Rachel" value={name} onChange={e => setName(e.target.value)} autoFocus />
            <AuthInput
              label="Work email"
              type="email"
              placeholder="rachel@acme.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              hint={email && !isWorkEmail ? "Are you sure you want to use a personal email? Go/ links work best in teams, but feel free to use a personal email if you like." : undefined}
            />
            <AuthInput label="Password" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
            {error && (
              <p className="text-[13px] mb-3" style={{ color: 'var(--color-peach-deep)' }}>{error}</p>
            )}
            <C2Button variant="accent" size="lg" className="w-full mt-2" type="submit" disabled={loading}>
              {loading ? '…' : 'Create account'}
            </C2Button>
          </form>
        </>
      )}

      <div
        className="mt-6 pt-5 border-t border-hairline text-center text-sm text-ink-soft"
      >
        Already have an account?{' '}
        <Link to="/login" className="font-semibold no-underline" style={{ color: 'var(--accent-solid)' }}>
          Sign in
        </Link>
      </div>
    </AuthShell>
  );
}
